import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("rh_token"));
  const [loading, setLoading] = useState(true);

  // Wire axios default header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("rh_token", token);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("rh_token");
    }
  }, [token]);

  // Fetch current user on mount if token exists
  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`);
        setUser(res.data);
      } catch (e) {
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setToken(res.data.access_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
  };

  const refreshUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  // Permission helper. Admins always pass.
  const can = (screen, action) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const perms = user.permissions || {};
    const list = perms[screen] || [];
    return list.includes(action);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser, can }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
